import { forwardRef, useMemo, type ReactNode } from 'react';
import { UseFormReturnType } from '@mantine/form';
import TextInput from '../inputs/TextInput';
import PasswordInput from '../inputs/PasswordInput';
import Select from '../inputs/Select';
import MultiSelect from '../inputs/MultiSelect';
import Checkbox from '../inputs/Checkbox';
import Switch from '../inputs/Switch';
import Slider from '../inputs/Slider';
import Rating from '../inputs/Rating';
import Textarea from '../inputs/Textarea';
import HtmlEditor from '../inputs/HtmlEditor';
import CompactHtmlEditor from '../inputs/CompactHtmlEditor';
import { Text } from '../core';
import { Stack } from '../layout';
import { Icon } from '../icons/Icon';
import { getTranslator, type LocaleKey } from '../../../localize';
import { usePreferencesStore } from '../../../stores';
import { containsEmail, isEmail, isUrl, maxLength, minLength } from '../../../utils/validation';

export type FormFieldType =
  | 'text'
  | 'email'
  | 'password'
  | 'number'
  | 'textarea'
  | 'select'
  | 'multiselect'
  | 'checkbox'
  | 'switch'
  | 'radio'
  | 'file'
  | 'date'
  | 'datetime'
  | 'search'
  | 'slider'
  | 'rating'
  | 'editor'
  | 'richtext';

export type FormFieldOption = { value: string; label: string };

export type ValidationRule =
  | { type: 'email'; messageKey?: string }
  | { type: 'url'; messageKey?: string }
  | { type: 'containsEmail'; messageKey?: string }
  | { type: 'minLength'; value: number; messageKey?: string }
  | { type: 'maxLength'; value: number; messageKey?: string }
  | { type: 'custom'; validate: (value: any, values: Record<string, any>) => string | null };

export interface FormFieldConfig {
  name: string;
  label?: string;
  labelKey?: string;
  placeholder?: string;
  placeholderKey?: string;
  description?: string;
  descriptionKey?: string;
  helpText?: string;
  helpTextKey?: string;
  required?: boolean;
  disabled?: boolean;
  defaultValue?: any;
  options?: FormFieldOption[];
  validation?: (value: any, values: Record<string, any>) => string | null;
  rules?: ValidationRule[];
  min?: number;
  max?: number;
  step?: number;
  rows?: number;
  accept?: string;
  multiple?: boolean;
  span?: number;
  visible?: (values: Record<string, any>) => boolean;
  leftSection?: ReactNode;
  rightSection?: ReactNode;
  type: FormFieldType;
}

export interface FormFieldProps {
  field: FormFieldConfig;
  form: UseFormReturnType<Record<string, any>>;
  compact?: boolean;
  disabled?: boolean;
}

const FormField = forwardRef<HTMLDivElement, FormFieldProps>(({ field, form, compact = false, disabled = false }, ref) => {
  const language = usePreferencesStore((state) => state.language);
  const locale = (language as LocaleKey) || 'en';
  const t = useMemo(() => getTranslator(locale), [locale]);

  if (field.visible && !field.visible(form.values)) {
    return null;
  }

  const label = resolveText(t, field.labelKey, field.label ?? field.name);
  const placeholder = resolveText(t, field.placeholderKey, field.placeholder);
  const description = resolveText(t, field.descriptionKey, field.description);
  const helpText = resolveText(t, field.helpTextKey, field.helpText);

  const inputProps = form.getInputProps(field.name);
  const errorText = typeof inputProps.error === 'string' ? resolveText(t, undefined, inputProps.error) : undefined;
  const size = compact ? 'sm' : 'md';

  const commonProps = {
    label,
    placeholder,
    description,
    required: field.required,
    disabled: field.disabled || disabled,
    size,
    leftSection: field.leftSection,
    rightSection: field.rightSection,
    ...inputProps,
  };

  const renderField = () => {
    const ratingSize = compact ? 18 : 22;

    switch (field.type) {
      case 'text':
      case 'email':
        return <TextInput {...commonProps} type={field.type} />;
      case 'password':
        return <PasswordInput {...commonProps} />;
      case 'number':
        return <TextInput {...commonProps} type="number" min={field.min} max={field.max} step={field.step} />;
      case 'textarea':
        return <Textarea {...commonProps} minRows={field.rows || 4} autosize />;
      case 'select':
        return <Select {...commonProps} data={field.options || []} clearable />;
      case 'multiselect':
        return <MultiSelect {...commonProps} data={field.options || []} searchable clearable />;
      case 'checkbox':
        return (
          <Checkbox
            {...commonProps}
            {...form.getInputProps(field.name, { type: 'checkbox' })}
            label={label}
          />
        );
      case 'switch':
        return (
          <Switch
            {...commonProps}
            {...form.getInputProps(field.name, { type: 'checkbox' })}
            label={label}
          />
        );
      case 'radio':
        return (
          <Stack gap="xs">
            {field.options?.map((option) => (
              <Checkbox
                key={option.value}
                label={option.label}
                value={option.value}
                checked={form.values[field.name] === option.value}
                onChange={(event) => form.setFieldValue(field.name, event.currentTarget.value)}
              />
            ))}
          </Stack>
        );
      case 'file':
        return <TextInput {...commonProps} type="file" accept={field.accept} multiple={field.multiple} />;
      case 'date':
        return <TextInput {...commonProps} type="date" />;
      case 'datetime':
        return <TextInput {...commonProps} type="datetime-local" />;
      case 'search':
        return <TextInput {...commonProps} type="search" rightSection={<Icon name="search" size="sm" aria-label="Search" />} />;
      case 'slider':
        return <Slider {...commonProps} min={field.min} max={field.max} step={field.step} />;
      case 'rating':
        return (
          <Rating
            value={form.values[field.name]}
            onChange={(val) => form.setFieldValue(field.name, val)}
            size={ratingSize}
            readOnly={field.disabled || disabled}
          />
        );
      case 'editor':
      case 'richtext':
        return <HtmlEditor {...commonProps} />;
      default:
        return <TextInput {...commonProps} />;
    }
  };

  return (
    <Stack ref={ref} gap={4}>
      {renderField()}
      {helpText && (
        <Text size="sm" c="dimmed">
          {helpText}
        </Text>
      )}
      {errorText && (
        <Text size="sm" c="red.6">
          {errorText}
        </Text>
      )}
    </Stack>
  );
});

FormField.displayName = 'FormField';

export default FormField;

export function buildValidator(field: FormFieldConfig, t: ReturnType<typeof getTranslator>) {
  return (value: any, values: Record<string, any>) => {
    if (field.required) {
      const empty = value === undefined || value === null || value === '' || (Array.isArray(value) && value.length === 0);
      if (empty) return t('components.forms.required', { label: field.label || field.name }) || `${field.label || field.name} is required`;
    }

    if (field.validation) {
      return field.validation(value, values);
    }

    if (!field.rules) return null;

    for (const rule of field.rules) {
      switch (rule.type) {
        case 'email':
          if (!isEmail(String(value || ''))) {
            return resolveText(t, rule.messageKey, 'Invalid email');
          }
          break;
        case 'url':
          if (!isUrl(String(value || ''))) {
            return resolveText(t, rule.messageKey, 'Invalid URL');
          }
          break;
        case 'containsEmail':
          if (!containsEmail(String(value || ''))) {
            return resolveText(t, rule.messageKey, 'Must contain an email');
          }
          break;
        case 'minLength':
          if (!minLength(String(value || ''), rule.value)) {
            return resolveText(t, rule.messageKey, `Minimum ${rule.value} characters`);
          }
          break;
        case 'maxLength':
          if (!maxLength(String(value || ''), rule.value)) {
            return resolveText(t, rule.messageKey, `Maximum ${rule.value} characters`);
          }
          break;
        case 'custom': {
          const result = rule.validate(value, values);
          if (result) return resolveText(t, undefined, result);
          break;
        }
        default:
          break;
      }
    }

    return null;
  };
}

function resolveText(
  t: ReturnType<typeof getTranslator>,
  key?: string,
  fallback?: string,
  replacements?: Record<string, string | number>
) {
  if (key) return t(key, replacements);
  return fallback || '';
}
